import { type PropsWithChildren } from 'react';

import InlineMessage from '@ingka/inline-message';
import crossCircleIcon from '@ingka/ssr-icon/paths/cross-circle';
import { translate } from '@repo/phrase';
import { ApiError, ErrorCodes, isApiErrorCode, isApiErrorData, isNotFoundError } from '@repo/utils';

import { NoBodyMessage } from '../message/message.component';

import styles from './styles.module.scss';

interface DefaultMessageProps {
    textKey?: string | undefined;
}

export interface QueryErrorProps extends DefaultMessageProps {
    error: unknown;
}

const DefaultMessage = ({ textKey }: DefaultMessageProps) => {
    return (
        <InlineMessage
            title={translate('error_fetching_title')}
            body={translate(textKey ?? 'error_fetching_body')}
            ssrIcon={crossCircleIcon}
            variant="negative"
        />
    );
};

const ApiErrorMessage = ({ error }: { error: ApiError }) => {
    if (isNotFoundError(error)) {
        return (
            <NoBodyMessage
                title={translate('error_asset_not_found_title')}
                body={translate('error_asset_not_found_body')}
            />
        );
    }

    if (isApiErrorData(error.data) && isApiErrorCode(error.data.errorCode)) {
        const errorKey = ErrorCodes[error.data.errorCode];
        return (
            <InlineMessage
                title={translate(`${errorKey}_title`)}
                body={translate(`${errorKey}_body`)}
                ssrIcon={crossCircleIcon}
                variant="negative"
            />
        );
    }

    return <DefaultMessage />;
};

const QueryErrorWrapper = ({ children, error }: QueryErrorProps & PropsWithChildren) => {
    return (
        <div className={styles.container} data-testid="query-error">
            {children}
            {import.meta.env.VITE_ENV === 'local' && (
                <p style={{ marginTop: 8 }}>Local DEV error: {JSON.stringify(error)}</p>
            )}
        </div>
    );
};

export const QueryError = ({ error, textKey }: QueryErrorProps) => {
    if (error instanceof ApiError) {
        return (
            <QueryErrorWrapper error={error}>
                <ApiErrorMessage error={error} />
            </QueryErrorWrapper>
        );
    }

    return (
        <QueryErrorWrapper error={error}>
            <DefaultMessage textKey={textKey} />
        </QueryErrorWrapper>
    );
};
